/**
 * 朝堂面板组件
 * 显示官职体系和朝议决策
 */

import { useState } from 'react'
import { useCourtStore } from '../../stores/courtStore'
import { SHANG_FACTIONS } from '../../data/shangDynastyData'
import './CourtPanel.css'

interface CourtPanelProps {
  factionId: string
}

export function CourtPanel({ factionId }: CourtPanelProps) {
  const [activeTab, setActiveTab] = useState<'positions' | 'council' | 'policies' | 'factions'>('positions')
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null)
  
  const { 
    governmentSystem, 
    positions, 
    officials, 
    councilSessions,
    policies,
    factions,
    appointOfficial,
    createCouncilSession
  } = useCourtStore()
  
  const faction = SHANG_FACTIONS.find(f => f.id === factionId)
  
  // 模拟人物数据
  const mockCharacters = [
    { id: 'char1', name: '姜子牙', ability: 95, loyalty: 98 },
    { id: 'char2', name: '周公旦', ability: 90, loyalty: 95 },
    { id: 'char3', name: '召公奭', ability: 85, loyalty: 92 },
    { id: 'char4', name: '毕公高', ability: 80, loyalty: 90 },
    { id: 'char5', name: '康叔封', ability: 75, loyalty: 88 }
  ]
  
  // 处理任命官员
  const handleAppointOfficial = (positionId: string, characterId: string) => {
    appointOfficial(characterId, positionId, factionId)
  }
  
  // 处理创建朝议
  const handleCreateCouncil = () => {
    createCouncilSession({
      type: 'policy',
      title: '是否对东夷用兵？',
      description: '东夷部落频繁侵扰边境，是否出兵讨伐？',
      proposer: 'player',
      votes: [],
      effects: {
        military: -10,
        economy: -5,
        stability: -3
      }
    })
  }
  
  if (!faction) {
    return <div className="court-panel">未找到势力信息</div>
  }
  
  return (
    <div className="court-panel">
      {/* 标签页 */}
      <div className="court-tabs">
        <button 
          className={`court-tab ${activeTab === 'positions' ? 'active' : ''}`}
          onClick={() => setActiveTab('positions')}
        >
          官职体系
        </button>
        <button 
          className={`court-tab ${activeTab === 'council' ? 'active' : ''}`}
          onClick={() => setActiveTab('council')}
        >
          朝议大厅
        </button>
        <button 
          className={`court-tab ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          国策
        </button>
        <button 
          className={`court-tab ${activeTab === 'factions' ? 'active' : ''}`}
          onClick={() => setActiveTab('factions')}
        >
          派系
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="court-content">
        {/* 官职体系 */}
        {activeTab === 'positions' && (
          <div className="positions-section">
            <div className="section-header">
              <h3>官职体系</h3>
              <div className="system-info">
                <span className="system-label">当前官制：</span>
                <span className="system-value">{getSystemName(governmentSystem)}</span>
                <button className="system-change-btn">更换官制</button>
              </div>
            </div>
            
            <div className="positions-grid">
              {Array.from(positions.values()).map(position => {
                const official = officials.get(position.id)
                const character = mockCharacters.find(c => c.id === official?.characterId)
                
                return (
                  <div 
                    key={position.id}
                    className={`position-card ${official ? 'filled' : 'vacant'}`}
                    onClick={() => setSelectedPosition(position.id)}
                  >
                    <div className="position-header">
                      <h4>{position.name}</h4>
                      <span className="position-rank">{getRankName(position.rank)}</span>
                    </div>
                    
                    {official && character ? (
                      <div className="official-info">
                        <div className="character-name">{character.name}</div>
                        <div className="character-stats">
                          <span>能力: {character.ability}</span>
                          <span>忠诚: {character.loyalty}</span>
                        </div>
                        <div className="performance-bar">
                          <div 
                            className="performance-fill"
                            style={{ width: `${official.performance.rating}%` }}
                          />
                          <span>{official.performance.rating}/100</span>
                        </div>
                      </div>
                    ) : (
                      <div className="vacant-info">
                        <span className="vacant-icon">👤</span>
                        <span className="vacant-text">空缺</span>
                      </div>
                    )}
                    
                    <div className="position-actions">
                      {!official && (
                        <select 
                          className="appoint-select"
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAppointOfficial(position.id, e.target.value)
                            }
                          }}
                        >
                          <option value="">任命官员</option>
                          {mockCharacters.map(char => (
                            <option key={char.id} value={char.id}>
                              {char.name} (能力: {char.ability}, 忠诚: {char.loyalty})
                            </option>
                          ))}
                        </select>
                      )}
                      {official && (
                        <button className="action-btn">罢免</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        
        {/* 朝议大厅 */}
        {activeTab === 'council' && (
          <div className="council-section">
            <div className="section-header">
              <h3>朝议大厅</h3>
              <button 
                className="create-council-btn"
                onClick={handleCreateCouncil}
              >
                发起朝议
              </button>
            </div>
            
            <div className="council-list">
              {Array.from(councilSessions.values()).map(session => (
                <div key={session.id} className="council-card">
                  <div className="council-header">
                    <h4>{session.title}</h4>
                    <span className={`council-status ${session.status}`}>
                      {getStatusText(session.status)}
                    </span>
                  </div>
                  <p className="council-description">{session.description}</p>
                  <div className="council-footer">
                    <span className="council-proposer">提案人: {session.proposer}</span>
                    <span className="council-date">{formatDate(session.proposedAt)}</span>
                  </div>
                </div>
              ))}
              
              {Array.from(councilSessions.values()).length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📋</span>
                  <p>暂无朝议</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 国策 */}
        {activeTab === 'policies' && (
          <div className="policies-section">
            <div className="section-header">
              <h3>国策</h3>
              <button className="enact-policy-btn">制定国策</button>
            </div>
            
            <div className="policies-list">
              {Array.from(policies.values()).map(policy => (
                <div key={policy.id} className={`policy-card ${policy.status}`}>
                  <div className="policy-header">
                    <h4>{policy.name}</h4>
                    <span className={`policy-status ${policy.status}`}>
                      {policy.status === 'active' ? '生效中' : '已废除'}
                    </span>
                  </div>
                  <p className="policy-description">{policy.description}</p>
                  <div className="policy-effects">
                    <h5>效果：</h5>
                    <div className="effects-grid">
                      <EffectItem label="稳定度" value={policy.effects.stability} />
                      <EffectItem label="人口" value={policy.effects.population} />
                      <EffectItem label="经济" value={policy.effects.economy} />
                      <EffectItem label="军事" value={policy.effects.military} />
                      <EffectItem label="文化" value={policy.effects.culture} />
                      <EffectItem label="外交" value={policy.effects.diplomacy} />
                    </div>
                  </div>
                </div>
              ))}
              
              {Array.from(policies.values()).length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📜</span>
                  <p>暂无国策</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 派系 */}
        {activeTab === 'factions' && (
          <div className="factions-section">
            <div className="section-header">
              <h3>派系</h3>
              <button className="create-faction-btn">创建派系</button>
            </div>
            
            <div className="factions-list">
              {factions.map(faction => (
                <div key={faction.id} className="faction-card">
                  <div className="faction-header">
                    <h4>{faction.name}</h4>
                    <span className="faction-influence">
                      影响力: {faction.influence}
                    </span>
                  </div>
                  <div className="faction-leader">
                    <span className="leader-label">领袖: </span>
                    <span className="leader-name">{faction.leader}</span>
                  </div>
                  <div className="faction-members">
                    <span className="members-label">成员: </span>
                    <span className="members-count">{faction.members.length} 人</span>
                  </div>
                </div>
              ))}
              
              {factions.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">👥</span>
                  <p>暂无派系</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* 选中官职详情 */}
      {selectedPosition && (
        <div className="position-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>官职详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedPosition(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助组件
interface EffectItemProps {
  label: string
  value: number
}

function EffectItem({ label, value }: EffectItemProps) {
  return (
    <div className="effect-item">
      <span className="effect-label">{label}</span>
      <span className={`effect-value ${value >= 0 ? 'positive' : 'negative'}`}>
        {value >= 0 ? `+${value}` : value}
      </span>
    </div>
  )
}

// 辅助函数
function getSystemName(system: string): string {
  const systemNames = {
    three_dukes: '三公九卿制',
    three_departments: '三省六部制',
    central_local: '中央集权+郡县制',
    feudal: '分封制',
    military: '军功爵制',
    theocratic: '神权制'
  }
  return systemNames[system as keyof typeof systemNames] || '未知官制'
}

function getRankName(rank: string): string {
  const rankNames = {
    first_rank: '一品',
    second_rank: '二品',
    third_rank: '三品',
    fourth_rank: '四品',
    fifth_rank: '五品',
    sixth_rank: '六品',
    seventh_rank: '七品',
    eighth_rank: '八品',
    ninth_rank: '九品'
  }
  return rankNames[rank as keyof typeof rankNames] || '未知品级'
}

function getStatusText(status: string): string {
  const statusTexts = {
    proposed: '已提案',
    voting: '投票中',
    passed: '通过',
    rejected: '拒绝',
    executing: '执行中',
    completed: '完成'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function formatDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}年${date.month}月${date.day}日`
}
